import 'intersection-observer'
import 'lib/i18n'

import i18next from 'i18next'
import { useSelector } from 'react-redux'
import {
  Fragment,
  lazy,
  PropsWithChildren,
  Suspense,
  useEffect,
  useRef,
} from 'react'
import { isNil } from 'lodash'
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  NavigateProps,
  Outlet,
} from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { supportLangs } from 'config'
import { ROUTE_PATH } from 'lib/paths'
import { useCountMeaningfulTime, useWebsiteInitializer } from 'hooks'
import useClearZombieConferenceCall from 'hooks/useClearZombieCall'
import withStripeElementProvider from 'features/Payment/hocs/withStripeElementProvider'
import { getI18nPath } from 'features/i18n/router'
import { selectIsLoggedIn } from 'store/selectors'
import { useRemoteConfig } from 'store/slices/remoteConfig'
import { useCurrentUser } from 'store/api/yay/user'

import MainLayout from 'layouts/MainLayout'
import PageLoading from 'components/Loading/PageLoading'

import '../stylesheets/application.scss'

import { useFeature } from '@growthbook/growthbook-react'

// Non-MainLayout routes
const HomePage = lazy(
  () =>
    import(
      /* webpackChunkName: "HomePage" */
      '../pages/Home'
    )
)
const Conference = lazy(
  () =>
    import(
      /* webpackChunkName: "Conference" */
      '../pages/Conference'
    )
)
const CancelSubscriptionPlanPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CancelSubscriptionPlanPage" */
      '../features/VipCancellation'
    )
)
const NotFoundPage = lazy(
  () =>
    import(
      /* webpackChunkName: "NotFoundPage" */
      '../pages/NotFoundPage'
    )
)

// MainLayout routes
const Login = lazy(
  () =>
    import(
      /* webpackChunkName: "Login" */
      '../pages/Login'
    )
)
const LoginCallback = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginCallback" */
      '../pages/LoginCallback'
    )
)
const LoginCallbackLine = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginCallbackLine" */
      '../pages/LoginCallbackLine'
    )
)
const LoginCallbackApple = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginCallbackApple" */
      '../pages/LoginCallbackApple'
    )
)
const LoginCallbackGoogle = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginCallbackGoogle" */
      '../pages/LoginCallbackGoogle'
    )
)
const LoginCallbackWorldcoin = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginCallbackWorldcoin" */
      '../pages/LoginCallbackWorldcoin'
    )
)
const DotMoneyLoginCallback = lazy(
  () =>
    import(
      /* webpackChunkName: "DotMoneyLoginCallback" */
      '../pages/DotMoneyLoginCallback'
    )
)
const ResetPassword = lazy(
  () =>
    import(
      /* webpackChunkName: "ResetPassword" */
      '../pages/ResetPassword'
    )
)
const ConfirmedEmail = lazy(
  () =>
    import(
      /* webpackChunkName: "ConfirmedEmail" */
      '../pages/ConfirmedEmail'
    )
)
const Timeline = lazy(
  () =>
    import(
      /* webpackChunkName: "Timeline" */
      '../pages/Timeline'
    )
)
const TimelineTestingTools = lazy(
  () =>
    import(
      /* webpackChunkName: "TimelineTestingTools" */
      '../pages/TimelineTestingTools'
    )
)
const Chat = lazy(
  () =>
    import(
      /* webpackChunkName: "Chat" */
      '../pages/Chat'
    )
)
const Hashtag = lazy(
  () =>
    import(
      /* webpackChunkName: "Hashtag" */
      '../pages/Hashtag'
    )
)
const HashtagsSearch = lazy(
  () =>
    import(
      /* webpackChunkName: "HashtagsSearch" */
      '../pages/SearchPage/HashtagsSearch'
    )
)
const SearchPage = lazy(
  () =>
    import(
      /* webpackChunkName: "SearchPage" */
      '../pages/SearchPage'
    )
)
const PostsSearch = lazy(
  () =>
    import(
      /* webpackChunkName: "PostsSearch" */
      '../pages/SearchPage/PostsSearch'
    )
)
const Post = lazy(
  () =>
    import(
      /* webpackChunkName: "Post" */
      '../pages/Post'
    )
)
const PostReposts = lazy(
  () =>
    import(
      /* webpackChunkName: "PostReposts" */
      '../pages/PostReposts'
    )
)
const PostGifts = lazy(
  () =>
    import(
      /* webpackChunkName: "PostGifts" */
      '../pages/PostGifts'
    )
)
const Conversations = lazy(
  () =>
    import(
      /* webpackChunkName: "Conversations" */
      '../pages/Conversations'
    )
)
const Group = lazy(
  () =>
    import(
      /* webpackChunkName: "Group" */
      '../pages/Group'
    )
)
const GroupHome = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupHome" */
      '../pages/Group/GroupHome'
    )
)
const GroupDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupDetail" */
      '../pages/Group/GroupDetail'
    )
)
const GroupGifts = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupGifts" */
      '../pages/Group/GroupGifts'
    )
)
const GroupInformation = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupInformation" */
      '../pages/Group/GroupInformation'
    )
)
const GroupSearch = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupSearch" */
      '../pages/Group/GroupSearch'
    )
)
const GroupSearchPost = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupSearchPost" */
      '../pages/Group/GroupSearch/GroupSearchPost'
    )
)
const GroupSearchThreadPost = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupSearchThreadPost" */
      '../pages/Group/GroupSearch/GroupSearchThreadPost'
    )
)
const GroupThreads = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupThreads" */
      '../pages/Group/GroupThreads'
    )
)
const GroupThread = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupThread" */
      '../pages/Group/GroupThread'
    )
)
const GroupThreadPost = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupThreadPost" */
      '../pages/Group/GroupThreadPost'
    )
)
const GroupThreadInfo = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupThreadInfo" */
      '../pages/Group/GroupThreadInfo'
    )
)
const GroupInvitePage = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupInvitePage" */
      '../pages/Group/Invite'
    )
)
const GroupAlbum = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupAlbum" */
      '../pages/Group/GroupAlbum'
    )
)
const GroupHighlights = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupHighlights" */
      '../pages/Group/GroupHighlights'
    )
)
const GroupWalkthrough = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupWalkthrough" */
      '../pages/Group/GroupWalkthrough'
    )
)
const Groups = lazy(
  () =>
    import(
      /* webpackChunkName: "Groups" */
      '../pages/Groups'
    )
)
const GroupsRanking = lazy(
  () =>
    import(
      /* webpackChunkName: "Groups" */
      '../pages/GroupsRanking'
    )
)
const BookmarksPage = lazy(
  () =>
    import(
      /* webpackChunkName: "BookmarksPage" */
      '../pages/Bookmarks'
    )
)
const EMPLDetails = lazy(
  () =>
    import(
      /* webPackChunkName: "EMPLDetails" */
      '../pages/EMPLDetails'
    )
)
const EMPLDetailsHistory = lazy(
  () =>
    import(
      /* webPackChunkName: "EMPLDetailsHistory" */
      '../pages/EMPLDetailsHistory'
    )
)
const EMPLDetailsExpiry = lazy(
  () =>
    import(
      /* webPackChunkName: "EMPLDetailsExpiry" */
      '../pages/EMPLDetailsExpiry'
    )
)
const AccountSetting = lazy(
  () =>
    import(
      /* webpackChunkName: "AccountSetting" */
      '../pages/AccountSetting'
    )
)
const UserSettingModal = lazy(
  () =>
    import(
      /* webpackChunkName: "UserSettingModal" */
      '../features/Setting/SettingModal'
    )
)
const PrivacySetting = lazy(
  () =>
    import(
      /* webpackChunkName: "PrivacySetting" */
      '../features/PrivacySetting'
    )
)
const InterestsSelectionPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InterestsSelectionPage" */
      '../pages/InterestsSelection'
    )
)
const DataManagementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "DataManagementPage" */
      '../pages/DataManagement'
    )
)
const ChatManagementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ChatManagementPage" */
      '../pages/ChatManagement'
    )
)
const ManageHiddenChatsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ManageHiddenChatsPage" */
      '../pages/ChatManagement/Hidden'
    )
)
const UserManagementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "UserManagementPage" */
      '../pages/UserManagement'
    )
)
const ManageFollowRequestPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ManageFollowRequestPage" */
      '../pages/UserManagement/FollowerRequest'
    )
)
const ManageBlockListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ManageBlockListPage" */
      '../pages/UserManagement/BlockList'
    )
)
const ManageFollowersPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ManageFollowersPage" */
      '../pages/UserManagement/Followers'
    )
)
const ManageFollowingPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ManageFollowingPage" */
      '../pages/UserManagement/Following'
    )
)
const EmailSetting = lazy(
  () =>
    import(
      /* webpackChunkName: "EmailSetting" */
      '../features/UserSetting/EmailSetting'
    )
)
const PasswordSetting = lazy(
  () =>
    import(
      /* webpackChunkName: "PasswordSetting" */
      '../features/UserSetting/PasswordSetting'
    )
)
const PhoneSetting = lazy(
  () =>
    import(
      /* webpackChunkName: "PhoneSetting" */
      '../features/PhoneSetting'
    )
)
const AccountLinksSetting = lazy(
  () =>
    import(
      /* webpackChunkName: "AccountLinksSetting" */
      '../features/AccountLinksSetting'
    )
)
const InvitationSetting = lazy(
  () =>
    import(
      /* webpackChunkName: "InvitationSetting" */
      '../features/UserSetting/InvitationSetting'
    )
)
const TwoFaSetting = lazy(
  () =>
    import(
      /* webpackChunkName: "TwoFaSetting" */
      '../features/UserSetting/TwoFaSetting'
    )
)
const ReviewManagementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ReviewManagementPage" */
      '../pages/ReviewManagement'
    )
)
const OthersReviewManagementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "OthersReviewManagementPage" */
      '../pages/ReviewManagement/Others'
    )
)
const SelfReviewManagementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "SelfReviewManagementPage" */
      '../pages/ReviewManagement/Mine'
    )
)
const UserSubscriptionSetting = lazy(
  () =>
    import(
      /* webpackChunkName: "UserSubscriptionSetting" */
      '../features/UserSubscriptionSetting'
    )
)
const ChangeSubscriptionPlanPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ChangeSubscriptionPlanPage" */
      '../features/UserSubscriptionSetting/ChangeSubscriptionPlan'
    )
)
const AddCreditCardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "AddCreditCardPage" */
      '../features/UserSubscriptionSetting/AddCreditCard'
    )
)
const Ranking = lazy(
  () =>
    import(
      /* webpackChunkName: "Ranking" */
      '../pages/Ranking'
    )
)
const UsersSearch = lazy(
  () =>
    import(
      /* webpackChunkName: "UsersSearch" */
      '../pages/SearchPage/UsersSearch'
    )
)
const GroupsSearch = lazy(
  () =>
    import(
      /* webpackChunkName: "GroupsSearch" */
      '../pages/SearchPage/GroupsSearch'
    )
)
const UserInvitePage = lazy(
  () =>
    import(
      /* webpackChunkName: "UserInvitePage" */
      '../pages/User/Invite'
    )
)
const UserAlbum = lazy(
  () =>
    import(
      /* webpackChunkName: "UserAlbum" */
      '../pages/UserAlbum'
    )
)
const UserPage = lazy(
  () =>
    import(
      /* webpackChunkName: "User" */
      '../pages/User'
    )
)
const UserTimeline = lazy(
  () =>
    import(
      /* webpackChunkName: "UserTimeline" */
      '../pages/UserTimeline'
    )
)
const UserGifts = lazy(
  () =>
    import(
      /* webpackChunkName: "UserGifts" */
      '../pages/UserGifts'
    )
)
const UserReview = lazy(
  () =>
    import(
      /* webpackChunkName: "UserReview" */
      '../pages/UserReview'
    )
)
const UserFollower = lazy(
  () =>
    import(
      /* webpackChunkName: "UserFollower" */
      '../pages/UserFollower'
    )
)
const UserFollowing = lazy(
  () =>
    import(
      /* webpackChunkName: "UserFollowing" */
      '../pages/UserFollowing'
    )
)
const UserGroup = lazy(
  () =>
    import(
      /* webpackChunkName: "UserGroup" */
      '../pages/UserGroup'
    )
)
const Activities = lazy(
  () =>
    import(
      /* webpackChunkName: "Activities" */
      '../pages/Activities'
    )
)
const PhoneVerification = lazy(
  () =>
    import(
      /* webpackChunkName: "PhoneVerification" */
      '../features/PhoneVerification'
    )
)
const PhoneVerificationConfirmation = lazy(
  () =>
    import(
      /* webpackChunkName: "PhoneVerificationConfirmation" */
      '../features/PhoneVerificationConfirmation'
    )
)
const VipPurchasePage = lazy(
  () =>
    import(
      /* webpackChunkName: "VipPurchasePage" */
      '../pages/VipPurchasePage'
    )
)
const VipPurchasePaymentPage = lazy(
  () =>
    import(
      /* webpackChunkName: "VipPurchasePaymentPage" */
      '../pages/VipPurchasePage/VipPurchasePaymentPage'
    )
)
const TestnetAirdrop = lazy(
  () =>
    import(
      /* webpackChunkName: "TestnetAirdrop" */
      '../pages/TestnetAirdrop/TestnetAirdrop'
    )
)
const AuthorizeWallet = lazy(
  () =>
    import(
      /* webpackChunkName: "AuthorizeWallet" */
      '../pages/AuthorizeWallet'
    )
)
const VerifyDotMoney = lazy(
  () =>
    import(
      /* webpackChunkName: "VerifyDotMoney" */
      '../pages/VerifyDotMoney'
    )
)
const ExchangeGifts = lazy(
  () =>
    import(
      /* webpackChunkName: "ExchangeGifts" */
      '../pages/ExchangeGifts'
    )
)

const CheckAuth = ({
  children,
  auth = true,
  ...props
}: PropsWithChildren<
  Partial<NavigateProps> & {
    auth?: boolean
  }
>) => {
  const isLoggedIn = useSelector(selectIsLoggedIn)

  if (auth !== isLoggedIn) {
    return <Navigate to={ROUTE_PATH.LOGIN} replace {...props} />
  }

  return <>{children}</>
}

export const AppRoutes = withStripeElementProvider(() => {
  const location = useLocation()
  const navigate = useNavigate()
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const { data: currentUser } = useCurrentUser()
  const { is_web_subscription_enabled, is_web_phone_verification_enabled } =
    useRemoteConfig()

  const isEmplGiftsEnabled = useFeature('yay-17610')?.on

  const ___prevLocation = useRef(location.pathname)

  // Fetch any require things for website
  useWebsiteInitializer()

  // Clear conference call that user force close
  useClearZombieConferenceCall()

  // useStripePaymentStatusWatcher()
  useCountMeaningfulTime()

  useEffect(() => {
    const sentryGlobalScope = Sentry.getGlobalScope()

    sentryGlobalScope
      .setExtra('version', window?.gon?.version)
      .setTag('version', window?.gon?.version)

    if (currentUser?.id) {
      sentryGlobalScope
        .setExtra('userId', currentUser?.id)
        .setTag('userId', currentUser?.id)
    }
  }, [currentUser?.id])

  useEffect(() => {
    ;(async () => {
      const i18nPath = getI18nPath(location.pathname)

      if (i18nPath !== location.pathname) {
        navigate(`${i18nPath}${location.search}`, {
          replace: true,
        })

        return
      }

      const isMatchedUserTabPaths = (pathname: string) => {
        return !isNil(
          pathname?.match?.(new RegExp(ROUTE_PATH.USER('\\d+$'))) ||
            pathname?.match?.(new RegExp(ROUTE_PATH.USER_REVIEWS('\\d+'))) ||
            pathname?.match?.(new RegExp(ROUTE_PATH.USER_GROUPS('\\d+'))) ||
            pathname?.match?.(new RegExp(ROUTE_PATH.USER_FOLLOWERS('\\d+'))) ||
            pathname?.match?.(new RegExp(ROUTE_PATH.USER_FOLLOWINGS('\\d+')))
        )
      }

      // If a user navigates between tabs in user profile page, do not scroll to top.
      // https://jira.nanameue.jp/browse/YAY-14723
      if (
        !(
          isMatchedUserTabPaths(___prevLocation.current) &&
          isMatchedUserTabPaths(location.pathname)
        )
      ) {
        window.scrollTo(0, 0)
      }

      ___prevLocation.current = location.pathname
    })()
  }, [location.pathname, i18next.language])

  return (
    <Routes>
      {['/', ...supportLangs].map((lang) => (
        <Route
          path={lang === '/' ? '/' : `/${lang}`}
          key={lang}
          element={
            <Suspense fallback={<PageLoading />}>
              <Outlet />
            </Suspense>
          }
        >
          {!isLoggedIn && <Route index element={<HomePage />} />}

          <Route path="conference/:id" element={<Conference />} />
          <Route
            path="subscription/cancel"
            element={<CancelSubscriptionPlanPage />}
          />

          <Route path="" element={<MainLayout />}>
            <Route path="login" element={<Login />} />
            <Route path="login/callback" element={<LoginCallback />} />
            <Route path="login/callback/line" element={<LoginCallbackLine />} />
            <Route
              path="login/callback/apple"
              element={<LoginCallbackApple />}
            />
            <Route
              path="login/callback/google"
              element={<LoginCallbackGoogle />}
            />
            <Route
              path="login/callback/worldcoin"
              element={<LoginCallbackWorldcoin />}
            />

            <Route
              path="forgot-password"
              element={<Navigate to="/reset-password" replace />}
            />
            <Route path="reset-password" element={<ResetPassword />} />

            {/* should be email/confirmed, need to notify to app dev, if this changed */}
            <Route path="email_confirmed" element={<ConfirmedEmail />} />

            <Route path="chat" element={<Chat />} />

            <Route index element={<Timeline key="following" />} />
            <Route
              path="timeline"
              element={<Timeline key="open" isPublicTimeline />}
            />
            <Route
              path="timeline-testing-tools"
              element={
                <CheckAuth>
                  <TimelineTestingTools />
                </CheckAuth>
              }
            />
            <Route
              path="timeline/hashtag/:hashtag"
              element={<Hashtag key="timeline" />}
            />
            <Route path="post/:postId" element={<Post />} />
            <Route path="post/:postId/reposts" element={<PostReposts />} />
            <Route
              path="post/:postId/gifts"
              element={
                <CheckAuth>
                  <PostGifts />
                </CheckAuth>
              }
            />
            <Route
              path="conversation/:conversationId"
              element={<Conversations />}
            />

            <Route path="search" element={<SearchPage />}>
              <Route path="posts" element={<PostsSearch />} />
              <Route path="users" element={<UsersSearch />} />
              <Route path="groups" element={<GroupsSearch />} />
              <Route path="hashtags" element={<HashtagsSearch />} />
            </Route>

            <Route path="group/:groupId" element={<Group />}>
              <Route index element={<GroupHome />} />
              <Route path="album" element={<GroupAlbum />} />
              <Route path="detail" element={<GroupDetail />} />
              <Route path="information" element={<GroupInformation />} />
              <Route
                path="information/walkthrough"
                element={<GroupWalkthrough />}
              />
              <Route
                path="information/highlights"
                element={
                  <Navigate to="../../highlights" replace relative="path" />
                }
              />
              <Route path="highlights" element={<GroupHighlights />} />
              <Route path="search" element={<GroupSearch />}>
                <Route path="post" element={<GroupSearchPost />} />
                <Route path="thread-post" element={<GroupSearchThreadPost />} />
              </Route>
              <Route path="threads" element={<GroupThreads />} />
              <Route path="threads/:threadId" element={<GroupThread />}>
                <Route index element={<GroupThreadPost />} />
                <Route path="info" element={<GroupThreadInfo />} />
                <Route path="post/:postId" element={<Post />} />
                <Route path="post/:postId/reposts" element={<PostReposts />} />
                <Route
                  path="post/:postId/gifts"
                  element={
                    <CheckAuth>
                      <PostGifts />
                    </CheckAuth>
                  }
                />
                <Route
                  path="conversation/:conversationId"
                  element={<Conversations />}
                />
              </Route>
              <Route path="post/:postId" element={<Post />} />
              <Route path="post/:postId/reposts" element={<PostReposts />} />
              <Route
                path="post/:postId/gifts"
                element={
                  <CheckAuth>
                    <PostGifts />
                  </CheckAuth>
                }
              />
              <Route
                path="conversation/:conversationId"
                element={<Conversations />}
              />
            </Route>
            <Route
              path="group/:groupId/gifts"
              element={
                <CheckAuth>
                  <GroupGifts />
                </CheckAuth>
              }
            />

            <Route
              path="group/:groupId/invite"
              element={
                <CheckAuth auth={false} to=".." relative="path">
                  <GroupInvitePage />
                </CheckAuth>
              }
            />

            <Route path="groups" element={<Groups />} />
            <Route path="groups/all" element={<Groups />} />
            <Route path="groups/ranking" element={<GroupsRanking />} />
            <Route
              path="groups/category/:categoryId"
              element={<Groups isCategoryGroup />}
            />
            <Route path="bookmarks" element={<BookmarksPage />} />

            {isEmplGiftsEnabled && (
              <Fragment>
                <Route
                  path="user/empl-details"
                  element={
                    <CheckAuth>
                      <EMPLDetails />
                    </CheckAuth>
                  }
                />
                <Route
                  path="user/empl-details/history"
                  element={
                    <CheckAuth>
                      <EMPLDetailsHistory />
                    </CheckAuth>
                  }
                />
                <Route
                  path="user/empl-details/expiry/:emplType"
                  element={
                    <CheckAuth>
                      <EMPLDetailsExpiry />
                    </CheckAuth>
                  }
                />
              </Fragment>
            )}

            <Route
              path="user/setting"
              element={
                <CheckAuth>
                  <AccountSetting />
                </CheckAuth>
              }
            >
              <Route index element={<UserSettingModal />} />
              <Route path="privacy" element={<PrivacySetting />} />
              <Route path="faves" element={<InterestsSelectionPage />} />
              <Route path="data-management" element={<DataManagementPage />} />
              <Route path="chat-management" element={<ChatManagementPage />} />
              <Route
                path="chat-management/hidden"
                element={<ManageHiddenChatsPage />}
              />
              <Route path="user-management" element={<UserManagementPage />} />
              <Route
                path="user-management/follower-request"
                element={<ManageFollowRequestPage />}
              />
              <Route
                path="user-management/block"
                element={<ManageBlockListPage />}
              />
              <Route
                path="user-management/followers"
                element={<ManageFollowersPage />}
              />
              <Route
                path="user-management/following"
                element={<ManageFollowingPage />}
              />
              <Route path="email" element={<EmailSetting />} />
              {currentUser?.email && (
                <Route path="password" element={<PasswordSetting />} />
              )}
              {is_web_phone_verification_enabled && (
                <Route path="phone" element={<PhoneSetting />} />
              )}
              <Route path="links" element={<AccountLinksSetting />} />
              <Route path="invitation" element={<InvitationSetting />} />
              <Route path="2fa" element={<TwoFaSetting />} />

              <Route path="review" element={<ReviewManagementPage />}>
                <Route index element={<OthersReviewManagementPage />} />
                <Route path="others" element={<SelfReviewManagementPage />} />
              </Route>

              <Route
                path="subscription"
                element={<UserSubscriptionSetting />}
              />
              <Route
                path="subscription/change"
                element={<ChangeSubscriptionPlanPage />}
              />
              <Route path="credit-card/new" element={<AddCreditCardPage />} />
            </Route>

            <Route path="users/ranking/:mode" element={<Ranking />} />
            <Route
              path="user/:userId/invite"
              element={
                <CheckAuth auth={false} to=".." relative="path">
                  <UserInvitePage />
                </CheckAuth>
              }
            />

            <Route path="user/:userId" element={<UserPage />}>
              <Route index element={<UserTimeline />} />
              <Route path="reviews" element={<UserReview />} />
              <Route path="album" element={<UserAlbum />} />
              <Route path="followers" element={<UserFollower />} />
              <Route path="followings" element={<UserFollowing />} />
              <Route path="groups" element={<UserGroup />} />
            </Route>

            <Route
              path="user/:userId/gifts"
              element={
                <CheckAuth>
                  <UserGifts />
                </CheckAuth>
              }
            />

            <Route
              path="user/:userId/gifts/exchange"
              element={
                <CheckAuth>
                  <ExchangeGifts />
                </CheckAuth>
              }
            />

            <Route
              path="activities"
              element={<Activities key="notification" />}
            />
            <Route
              path="activities/reaction"
              element={<Activities key="reaction" />}
            />
            {is_web_phone_verification_enabled && (
              <>
                <Route
                  path="phone-verification"
                  element={
                    <CheckAuth>
                      <PhoneVerification />
                    </CheckAuth>
                  }
                />
                <Route
                  path="phone-verification/confirm"
                  element={
                    <CheckAuth>
                      <PhoneVerificationConfirmation />
                    </CheckAuth>
                  }
                />
              </>
            )}
            {is_web_subscription_enabled && (
              <>
                <Route
                  path="vip-purchase"
                  element={
                    <CheckAuth>
                      <VipPurchasePage />
                    </CheckAuth>
                  }
                />
                <Route
                  path="vip-purchase/:planId/payment"
                  element={
                    <CheckAuth>
                      <VipPurchasePaymentPage />
                    </CheckAuth>
                  }
                />
              </>
            )}

            <Route path="testnet-airdrop" element={<TestnetAirdrop />} />
            <Route path="authorize/wallet" element={<AuthorizeWallet />} />
            <Route path="dotmoney/login" element={<DotMoneyLoginCallback />} />
          </Route>

          <Route path="dotmoney/exchange/verify" element={<VerifyDotMoney />} />
        </Route>
      ))}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
})
